/**
 * Final Calculation Card for Accountant Report View
 *
 * Policy deductions block reads engine-outputs only.
 * Skipped = notes skip OR (empty waterfall && gross == 0) → NOT RUN.
 * stale=true badges "engine version changed — re-run" and still shows N.
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';
import FinancialValue from '../shared/FinancialValue';
import StateChip from '../shared/StateChip';
import {
  isPolicyDeductionsSkipped,
  type PolicyDeductions,
  type WaterfallStep,
} from './pickComputedSurfaces';

interface FinalCalculation {
  step1_vp_loss?: number;
  step2_incremental?: number;
  additional_expenses?: number;
  total_before_threshold?: number;
  final_claim?: number;
  accountant_fees?: number;
  continuing_expenses?: number;
  extra_expenses?: number;
  threshold_amount?: number;
  threshold_pct?: number;
  threshold_type?: string;
  policy_deductions?: PolicyDeductions;
}

interface Attestation {
  state: 'unattested' | 'attested';
  by?: string;
  at?: string;
}

interface FinalCalculationCardProps {
  finalCalculation: FinalCalculation;
  formatCurrency?: (amount?: number) => string;
  attestation?: Attestation;
  policyDeductions?: PolicyDeductions | null;
}

function asNumber(value: number | string | undefined): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function Row({
  label,
  formula,
  value,
  contra = false,
  final = false,
}: {
  label: string;
  formula?: React.ReactNode;
  value?: number;
  contra?: boolean;
  final?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto_150px] items-baseline content-center gap-6 px-5 h-11 border-t border-[var(--color-hairline)] ${
        final ? 'bg-[var(--color-bg-elevated)]' : ''
      }`}
    >
      <dt
        className={`text-[13px] truncate ${
          final
            ? 'font-semibold text-[var(--color-text-primary)]'
            : 'text-[var(--color-text-secondary)]'
        }`}
      >
        {label}
      </dt>
      <dd
        className="font-mono text-[11px] text-[var(--color-text-muted)] text-right"
        style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }}
      >
        {formula}
      </dd>
      <dd className="text-right">
        <FinancialValue
          value={value}
          contra={contra}
          className={
            final
              ? 'text-[15px] text-[var(--color-text-primary)]'
              : 'text-[13px] text-[var(--color-text-primary)]'
          }
        />
      </dd>
    </div>
  );
}

function isGrossStep(step: WaterfallStep): boolean {
  const blob = `${step.step ?? ''} ${step.description || ''}`.toLowerCase();
  return /gross/.test(blob) && !/deduct|waiting|adv|coinsur|limit|cap/.test(blob);
}

function PolicyDeductionWaterfall({ pd }: { pd: PolicyDeductions }) {
  if (isPolicyDeductionsSkipped(pd)) {
    return (
      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] mb-6">
        <header className="flex items-center justify-between gap-4 px-5 h-12">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
            Policy deductions → N
          </h2>
          <StateChip state="dormant" text="NOT RUN" mono />
        </header>
      </section>
    );
  }

  const rawSteps = Array.isArray(pd.deduction_waterfall) ? pd.deduction_waterfall : [];
  const steps = rawSteps[0] && isGrossStep(rawSteps[0]) ? rawSteps.slice(1) : rawSteps;
  const net = asNumber(pd.net_claim);
  const gross = asNumber(pd.gross_loss);
  const total = asNumber(pd.total_deductions);
  if (!rawSteps.length && net === undefined && gross === undefined) return null;

  return (
    <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] mb-6">
      <header className="flex items-center justify-between gap-4 px-5 h-12">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
          Policy deductions → N
        </h2>
        {pd.stale ? (
          <StateChip state="warning" text="engine version changed — re-run" mono />
        ) : (
          <StateChip state="verified" text="Engine output · not recomputed" mono />
        )}
      </header>

      <dl className="border-t border-[var(--color-border)]">
        <Row label="Gross loss" value={gross} formula="policy_deductions.gross_loss" />
        {steps.map((step, i) => {
          const amt = asNumber(step.deduction_amount);
          const bal = asNumber(step.running_balance);
          const label = step.description || (step.step != null ? String(step.step) : `Step ${i + 1}`);
          return (
            <Row
              key={`${step.step ?? label}-${i}`}
              label={label}
              value={amt}
              contra={(amt ?? 0) > 0}
              formula={
                bal !== undefined ? (
                  <>
                    bal <FinancialValue value={bal} compact />
                  </>
                ) : (
                  step.step != null ? String(step.step) : 'deduction'
                )
              }
            />
          );
        })}
        <Row label="Total deductions" value={total} contra formula="sum of steps" />
        <div aria-hidden="true" className="h-[5px] border-y border-[var(--color-border-strong)]" />
        <Row label="N · net_claim" final value={net} formula="gross − deductions" />
      </dl>
    </section>
  );
}

export default function FinalCalculationCard({
  finalCalculation,
  formatCurrency: _formatCurrency,
  attestation = { state: 'unattested' },
  policyDeductions,
}: FinalCalculationCardProps) {
  if (!finalCalculation) return null;
  const fc = finalCalculation;
  const pd = policyDeductions ?? fc.policy_deductions ?? null;

  const hasThreshold = (fc.threshold_amount ?? 0) > 0;

  const thresholdLabel =
    fc.threshold_pct != null
      ? `Less Threshold — ${fc.threshold_pct}%${fc.threshold_type ? ` ${fc.threshold_type}` : ''}`
      : 'Less Threshold';

  return (
    <>
      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] mb-6">
        <header className="flex items-center justify-between gap-4 px-5 h-12">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
            Final Calculation
          </h2>
          {attestation.state === 'attested' ? (
            <StateChip
              state="verified"
              text={`Attested${attestation.by ? ` · ${attestation.by}` : ''}${
                attestation.at ? ` · ${attestation.at}` : ''
              }`}
              mono
            />
          ) : (
            <StateChip state="needs-human" text="Unattested — needs review" />
          )}
        </header>

        <dl className="border-t border-[var(--color-border)]">
          <Row
            label="Step 1 · VP Loss"
            value={fc.step1_vp_loss}
            formula="Benchmark − Coverage"
          />
          <Row
            label="Step 2 · Incremental"
            value={fc.step2_incremental}
            formula={(fc.step2_incremental ?? 0) < 0 ? 'Decline adjustment' : 'Growth adjustment'}
          />
          <Row
            label="Step 3 · Additional Expenses"
            value={fc.additional_expenses}
            formula="Accounting + Continuing + Extra"
          />

          <div aria-hidden="true" className="border-t border-[var(--color-border-strong)]" />
          <Row
            label="Subtotal (Steps 1 + 2 + 3)"
            value={fc.total_before_threshold}
            formula={
              <>
                <FinancialValue value={fc.step1_vp_loss} compact />
                {' + '}
                <FinancialValue value={fc.step2_incremental} compact />
                {' + '}
                <FinancialValue value={fc.additional_expenses} compact />
              </>
            }
          />

          {hasThreshold && (
            <Row
              label={thresholdLabel}
              value={fc.threshold_amount}
              contra
              formula="Policy threshold"
            />
          )}

          <div aria-hidden="true" className="h-[5px] border-y border-[var(--color-border-strong)]" />
          <Row
            label="Final Claim"
            final
            value={fc.final_claim}
            formula={hasThreshold ? 'Subtotal − Threshold' : 'Subtotal'}
          />
        </dl>
      </section>

      {pd ? <PolicyDeductionWaterfall pd={pd} /> : null}

      <div className="mb-6">
        <CalculationMethodology
          section="final_calculation"
          data={{
            ...fc,
            accountant_fees: fc.accountant_fees ?? 0,
            continuing_expenses: fc.continuing_expenses ?? 0,
            extra_expenses: fc.extra_expenses ?? 0,
            threshold_amount: fc.threshold_amount ?? 0,
            threshold_pct: fc.threshold_pct ?? 0,
            threshold_type: fc.threshold_type ?? 'none',
          }}
        />
      </div>
    </>
  );
}
